// lib/modelist/light.ts
// A model's light (Fater3DLight): its defaults, its ranges, where its sun stands, and what the sun's elevation makes of
// the sunlight and the sky. Nothing here knows a renderer: the viewer and any other reader draw the same numbers.
import type { Fater3DLight, Fater3DPlace, Fater3DSavedLight, Fater3DState } from '#types/types';
import { heldInRanges } from '#lib/modelist/ranges';
import { DEFAULT_DATE, DEFAULT_PLACE, SUN_HORIZON, placeOf, solarPosition, sunInstant, zoneOffset } from '#lib/modelist/sun';
import { LIGHT_BACKDROPS, LIGHT_RANGES, LIGHT_SUNS, cleanLight, lightPrint, validDate, zoneName } from '../../scripts/modelist/light.mjs';

export { LIGHT_RANGES, zoneName };

/**
 * The light a project without one of its own opens on, and is captured under: the real sky at noon on DEFAULT_DATE,
 * over the project's place (defaultLightOver). Its angles, the pair not in force, are the studio key
 * (0.45, 0.8, 0.35): a switch to angles starts from it.
 */
export const LIGHT_DEFAULTS: Fater3DLight = { backdrop: 'sky', sun: 'time', azimuth: 127.875, elevation: 54.526, date: DEFAULT_DATE, time: 12 };

/** A change to the light as a control makes it: its numbers held in range, and without a choice or a date that is none. */
export const clampLight = (patch: Partial<Fater3DLight>): Partial<Fater3DLight> => {
  const next = heldInRanges<Fater3DLight>(LIGHT_RANGES, patch);
  if (next.backdrop !== undefined && !LIGHT_BACKDROPS.includes(next.backdrop)) delete next.backdrop;
  if (next.sun !== undefined && !LIGHT_SUNS.includes(next.sun)) delete next.sun;
  if (next.date !== undefined && !validDate(next.date)) delete next.date;
  return next;
};

/** A saved light read from outside the app: whole and in bounds, or null (scripts/modelist/light.mjs). */
export const parseLight = (value: unknown): Fater3DSavedLight | null => cleanLight(value) as Fater3DSavedLight | null;

type Lit = Pick<Fater3DState, 'light' | 'place' | 'movedPlace'>;

// The sun's angles in the model: its azimuth clockwise from the MODEL's north axis, so by date and time the place's
// north is added to the compass azimuth. The hour is read in the place's own zone, the same for every reader.
const sunOver = (light: Fater3DLight, place: Fater3DPlace): { azimuth: number; elevation: number } => {
  if (light.sun === 'angles') return { azimuth: light.azimuth, elevation: light.elevation };
  const sun = solarPosition(sunInstant(light.date, light.time, zoneOffset(place.zone, light.date)), place.lat, place.lon);
  return { azimuth: (sun.azimuth + place.north) % 360, elevation: sun.elevation };
};
/** The sun's angles of the light in force, over the place in force. */
export const sunAngles = (state: Lit) => sunOver(state.light, placeOf(state));

const toTheThousandth = (degrees: number) => Math.round(degrees * 1000) / 1000;
/**
 * A saved light with where its sun stands (Fater3DSavedLight.sunAt), worked out here whatever it carried: what the
 * app saves and what the server forwards. By date and time it is held to a thousandth of a degree, so two engines'
 * last digits do not make two suns.
 */
export const withSunAt = (saved: Fater3DSavedLight): Fater3DSavedLight => {
  if (saved.sun === 'time' && !saved.place) return saved;
  const at = sunOver(saved, saved.place!);
  return { ...saved, sunAt: saved.sun === 'angles' ? at : { azimuth: toTheThousandth(at.azimuth), elevation: toTheThousandth(at.elevation) } };
};

/**
 * LIGHT_DEFAULTS as a saved light, over a project's place, else DEFAULT_PLACE: the one definition the viewer opens
 * on, the server hands a capture, and a picture's light is judged against.
 */
export const defaultLightOver = (place: Fater3DPlace | null): Fater3DSavedLight => withSunAt({ ...LIGHT_DEFAULTS, place: place ?? DEFAULT_PLACE });

/** The setting of a saved light, what the panel holds: without the place it stands over nor its sun's resolved position. */
export const settingOf = ({ backdrop, sun, azimuth, elevation, date, time }: Fater3DSavedLight): Fater3DLight => ({ backdrop, sun, azimuth, elevation, date, time });

/** The light in force as it is saved: with the place it stands over when its sun is by date and time, and where its sun stands. */
export const savedLightOf = (state: Lit): Fater3DSavedLight => withSunAt(state.light.sun === 'time' ? { ...state.light, place: placeOf(state) } : state.light);

/**
 * Whether two lights are the same setting. The pair that is not in force is not compared: by its angles a light's
 * date and time say nothing, and by date and time its angles do not; nor does a place under a sun by its angles, nor
 * the sun's resolved position, which follows from the rest.
 */
export const sameLight = (a: Fater3DSavedLight, b: Fater3DSavedLight) => lightPrint({ ...a, sunAt: undefined }) === lightPrint({ ...b, sunAt: undefined });

/**
 * The elevations, in degrees, the sky turns on. Sunlight is whole from `full` up and white from `white` up; the day
 * sky gives way to the twilight sky between `duskFrom` and `duskTo`, three.js's day sky being black by 2.3° under the
 * horizon; the twilight sky then darkens to the night's, stars coming out, between `nightFrom` and `night`, the end of
 * civil twilight.
 */
export const SKY = { full: 8, white: 12, duskFrom: 2, duskTo: -2, nightFrom: 0, night: -6 } as const;

const smooth = (from: number, to: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - from) / (to - from)));
  return t * t * (3 - 2 * t);
};

/**
 * What the sun's elevation makes of the light, each a share from 0 to 1 that moves without a step:
 * `sun`, the sunlight left of a high sun's (none under SUN_HORIZON); `warmth`, how far its colour has gone from a high
 * sun's to a setting sun's; `dusk`, how much of the day sky the twilight sky has replaced; `night`, how far that
 * twilight sky has darkened to the night's, which is also how bright the stars are.
 */
export const skyAt = (elevation: number) => ({
  sun: elevation < SUN_HORIZON ? 0 : smooth(SUN_HORIZON, SKY.full, elevation),
  warmth: 1 - smooth(SUN_HORIZON, SKY.white, elevation),
  dusk: smooth(SKY.duskFrom, SKY.duskTo, elevation),
  night: smooth(SKY.nightFrom, SKY.night, elevation),
});

/**
 * The share of its sunlight the sun gives at an elevation. Under the sky it fades as the sun gets low; the studio's
 * key, a drawing's too, is whole until the sun is under the horizon, as a lamp is.
 */
export const sunShare = (sky: boolean, elevation: number) => (sky ? skyAt(elevation).sun : Number(elevation >= SUN_HORIZON));

/** Sunlight's colour, sRGB from 0 to 1: a high sun's warm white, a setting sun's orange (about 2200 K). */
export const SUNLIGHT = { high: [1, 0.945, 0.863], low: [1, 0.561, 0.239] } as const;
export const sunlightColour = (warmth: number): [number, number, number] =>
  [0, 1, 2].map((i) => SUNLIGHT.high[i]! + (SUNLIGHT.low[i]! - SUNLIGHT.high[i]!) * warmth) as [number, number, number];
